import classNames from 'classnames'
import {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import Badge from '../../components/core/badge'
import {ImgButton} from '../../components/core/button'
import ContentLoader from '../../components/core/loader'
import Modal from '../../components/core/modal'
import Table from '../../components/core/table'
import {PageTitle, ResponsivePageTitle} from '../../components/core/title'
import Tooltip from '../../components/core/tooltip'
import Footer from '../../components/layouts/footer'
import InstructionModal from '../../components/pages/job/Instructions'
import UrlContainer from '../../components/pages/job/Instructions/UrlContainer'
import {EditIcon, KeyIcon, LogManagementIcon, PlusIcon, SettingsIcon, TrashIcon} from '../../icons'
import {deleteLogGroup, getLogGroup, getLogGroups} from '../../stores/log-management'
import {colors} from '../../utils/colors'
import Customer from '../../utils/customer'
import DateHelper from '../../utils/date'
import TextHelper from '../../utils/text'
import EmptyContent from '../resources/EmptyContent'
import data from './data.json'
import text from './text.json'
import TeamSwitcher from '../../components/pages/teams'
import ActionButtons from '../../components/pages/listActionButtons'
import {Link} from 'react-router-dom'
import NavigationHelper from '../../utils/navigation'
import {routes} from '../../routes'
import Confirm from '../../components/core/confirm'
import {setConfirmIsActive} from '../../stores/alert'
import AddEditLogGroup from '../../components/pages/logManagement/AddEditLogGroup'
import modalTypes from '../../data/modals.json'
import {setActivePopup} from '../../stores/popup'

const customFieldLogic = {
    enabled: (item) => item?.enabled ? text.enabled : text.disabled
}

const LogGroupsList = () => {
    const dispatch = useDispatch()
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const services = useSelector(state => state.resources.services)
    const activePopup = useSelector(state => state.popup.activePopup)
    const {logGroups, isLogGroupFetching, logGroup} = useSelector(state => state.log_management)
    const [filteredLogGroups, setFilteredLogGroups] = useState([])
    const [selectedLogGroups, setSelectedLogGroups] = useState([])
    const [clickedLogGroup, setClickedLogGroup] = useState(null)
    const [isDeleting, setIsDeleting] = useState(false)

    const handlePopup = (modalData = null, selectedLogId = '') => {
        dispatch(setActivePopup(modalData ?
            {...modalData, log_group_id: selectedLogId}
            : null))
    }

    const deleteLogGroups = async() => {
        setIsDeleting(true)
        await dispatch(deleteLogGroup(clickedLogGroup || selectedLogGroups))
        setIsDeleting(false)
        setSelectedLogGroups([])
        setClickedLogGroup(null)
        dispatch(setConfirmIsActive(false))
    }

    const tableBody = filteredLogGroups?.map(item => {
        const retention = DateHelper.timeFormatter(item?.retention, 'days')?.formatted
        return [
            <span className="min-width-250" title={item?.name}>
                <Link className="underlined"
                    to={NavigationHelper.getNavigationUrl(routes.logs_dashboard.path, ':id', item?.id)}>
                    {TextHelper.textLengthChecker(item?.name, 60) || '-'}
                </Link>
            </span>,
            <span className="min-width-300" title={item?.description}>
                {TextHelper.textLengthChecker(item?.description, 36) || '-'}
            </span>,
            <span className="min-width-150">
                {retention}
            </span>,
            <span className=' gap-1 flex-1'>
                <Badge className="lowercase" color={item?.enabled ? text.success : text.warning}>
                    {item?.enabled ? text.enabled : text.disabled}
                </Badge>
                <span>
                    <Tooltip content={text.logs}>
                        <Link
                            className={classNames({
                                'table-action-btn': true,
                                dark: isDarkMode
                            })}
                            to={NavigationHelper.getNavigationUrl(routes.logs_dashboard.path, ':id', item?.id)}
                        >
                            <LogManagementIcon stroke={isDarkMode ? colors.colorFilterTitle : colors.colorMuted}/>
                        </Link>
                    </Tooltip>
                    <Tooltip content={text.instructions}>
                        <span
                            className={classNames({
                                'table-action-btn': true,
                                dark: isDarkMode
                            })}
                            onClick={() => handlePopup(modalTypes.log_management.instructions, item?.id)}
                        >
                            <SettingsIcon stroke={isDarkMode ? colors.colorFilterTitle : colors.colorMuted}/>
                        </span>
                    </Tooltip>
                    <ActionButtons
                        onEdit={() => handlePopup(modalTypes.log_management.editLogGroup, item?.id)}
                        onDelete={() => {
                            dispatch(setConfirmIsActive(true))
                            setClickedLogGroup(item?.id)
                        }}/>
                </span>
            </span>
        ]
    })

    const generateLogsTable = () => (
        isLogGroupFetching && !activePopup?.type ? <ContentLoader/> :
            filteredLogGroups?.length > 0 ? <div className='logs-table'>
                <Table
                    tableHeader={data.logs_table_header}
                    data={logGroups}
                    selectedData={selectedLogGroups}
                    identificator="id"
                    setSelectedData={setSelectedLogGroups}
                    tbodyData={tableBody}
                    isSortingEnabled
                    hasCheckbox={Customer.isOwnTeam()}
                    isMultiCheckable
                    setData={setFilteredLogGroups}
                    sortId={data.sort_id}
                    filteredData={filteredLogGroups}
                    customFieldLogic={customFieldLogic}
                />
            </div> :
                <EmptyContent message={text.no_data} hideButton/>
    )

    useEffect(() => {
        dispatch(getLogGroups())
    }, [services])

    useEffect(() => {
        setFilteredLogGroups(logGroups)
    }, [logGroups])

    useEffect(() => {
        if (activePopup?.log_group_id && activePopup?.type === modalTypes.log_management.instructions.type) {
            dispatch(getLogGroup(activePopup.log_group_id))
        }
    }, [activePopup?.log_group_id])

    return (
        <main className={classNames('main', {
            dark: isDarkMode
        })} id="log-management-log-groups">
            <Confirm
                body={text.are_you_sure}
                callback={deleteLogGroups} showLoader={isDeleting}/>
            {activePopup?.type &&
             <Modal isOpen={!!activePopup.type}
                 contentClassName={activePopup.type === modalTypes.log_management.instructions.type ? 'instruction-modal overflow-auto'
                     : ''
                 }
                 title={activePopup?.title}
                 closeModal={() => handlePopup()}>
                 {activePopup?.type === modalTypes.log_management.instructions.type && (isLogGroupFetching ?
                     <ContentLoader/> :
                     <InstructionModal
                         data={data.languages_examples}
                         urlTitle={text.section}
                         textTitle=''
                         options={{
                             desc: <UrlContainer
                                 url={logGroup?.api_key}
                                 showSeeInstructionsBtn={false}
                                 options={{
                                     urlContainerTitle: text.log_group_api_key,
                                     icon: <KeyIcon fill={isDarkMode ? colors.colorFilterTitle : colors.colorPrimary}/>
                                 }}
                             />
                         }}/>
                 ) }
                 {
                     activePopup?.type === modalTypes.log_management.addLogGroup.type &&
                     <AddEditLogGroup logGroupId={activePopup?.log_group_id} onCloseModal={handlePopup}/>
                 }
             </Modal>}
            <div>
                <ResponsivePageTitle />
                <div className="content-title">
                    <div className="left-side">
                        <div>
                            <span className={classNames('section', {
                                dark: isDarkMode
                            })}>{text.section}</span>
                            <PageTitle>
                                <h1  className={classNames('page-title', {
                                    dark: isDarkMode
                                })}>{text.title}</h1>
                            </PageTitle>
                        </div>
                    </div>
                    {Customer.isOwnTeam() && (
                        <ImgButton onClick={() => handlePopup(modalTypes.log_management.addLogGroup)}>
                            {text.add_log}
                            <span><PlusIcon/></span>
                        </ImgButton>
                    )}
                </div>
                <TeamSwitcher/>
                <>
                    <div className="search-filter-actions">
                        {Customer.isOwnTeam() && (
                            <div className="selected-actions">
                                <ImgButton onClick={() => handlePopup(modalTypes.log_management.addLogGroup)}
                                    className="responsive-add-button">
                                    <span><PlusIcon/></span>
                                </ImgButton>
                                <ImgButton className={classNames({
                                    'light-bg': !isDarkMode
                                })}  disabled={selectedLogGroups.length !== 1}
                                onClick={() => handlePopup(modalTypes.log_management.editLogGroup, selectedLogGroups?.[0])}>
                                    <span><EditIcon/></span>
                                    <span className="button-text">{text.edit}</span>
                                </ImgButton>
                                <ImgButton className={classNames('outline-red', {
                                    'light-bg': !isDarkMode
                                })}  disabled={selectedLogGroups.length === 0} onClick={() => dispatch(setConfirmIsActive(true))}>
                                    <span><TrashIcon stroke={colors.colorRed}/></span>
                                    <span className="button-text">{text.delete}</span>
                                </ImgButton>
                            </div>
                        )}
                    </div>
                    {generateLogsTable()}
                </>
            </div>
            <Footer />
        </main>
    )
}

export default LogGroupsList
